'use client'

import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { ReviewComment } from '@/components/ProductReviews/ReviewComment'
import { Skeleton } from '@/components/Skeleton/Skeleton'
import type { Review } from '@/features/memberReviews/memberReviews.types'
import { xStack } from 'ui/src/patterns/stack'
import {
  animatedWrapper,
  card,
  loadingTrack,
  root,
  scrollCardWrapper,
  scrollStrip,
  skeletonCard,
  track,
  trackItem,
} from './ReviewsSlideshow.css'

const MS_PER_CARD = 16000
const HOVER_PLAYBACK_RATE = 0.4

type Props = {
  reviews: Array<Review>
}

export function ReviewsSlideshow({ reviews }: Props) {
  const trackRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<Animation | null>(null)

  useEffect(() => {
    const trackElement = trackRef.current
    if (!trackElement || reviews.length === 0) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const animation = trackElement.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }], {
      duration: reviews.length * MS_PER_CARD,
      iterations: Infinity,
      easing: 'linear',
    })
    animationRef.current = animation

    return () => {
      animation.cancel()
      animationRef.current = null
    }
  }, [reviews.length])

  // Changing playbackRate keeps the current position, so the strip slows down smoothly.
  const handleMouseEnter = () => animationRef.current?.updatePlaybackRate(HOVER_PLAYBACK_RATE)
  const handleMouseLeave = () => animationRef.current?.updatePlaybackRate(1)

  if (reviews.length === 0) {
    return null
  }

  return (
    <div className={root}>
      <div className={animatedWrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div ref={trackRef} className={clsx(track, xStack({ gap: 'md', alignItems: 'stretch' }))}>
          {[...reviews, ...reviews].map((review, index) => (
            <div key={`${review.id}-${index}`} className={trackItem}>
              <ReviewComment className={card} {...review} restrictHeight={true} />
            </div>
          ))}
        </div>
      </div>

      <div className={scrollStrip}>
        {reviews.map((review) => (
          <div key={review.id} className={scrollCardWrapper}>
            <ReviewComment className={card} {...review} restrictHeight={true} />
          </div>
        ))}
      </div>
    </div>
  )
}

type LoadingProps = {
  count?: number
}

export function ReviewsSlideshowLoading({ count = 4 }: LoadingProps) {
  return (
    <div className={root}>
      <div className={animatedWrapper}>
        <div className={loadingTrack}>
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className={trackItem}>
              <Skeleton className={skeletonCard} />
            </div>
          ))}
        </div>
      </div>

      <div className={scrollStrip}>
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className={scrollCardWrapper}>
            <Skeleton className={skeletonCard} />
          </div>
        ))}
      </div>
    </div>
  )
}
